// EU-style (Europass-inspired) Resume Generator
// Uses browser print functionality for high-quality PDF output
// lang: 'en' (default) | 'de' (German)

export const generateProfilePDF = (user, dashboardData, isMasked = false, lang = 'en') => {
  // Create a new window for the printable profile
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Please allow popups to download your profile');
    return;
  }

  // Get photo URL
  let photoUrl = '';
  if (user?.photo_url) {
    let baseUrl = process.env.REACT_APP_API_URL || '';
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4);
    }
    photoUrl = user.photo_url.startsWith('http') ? user.photo_url : `${baseUrl}${user.photo_url}`;
  }

  // Labels by language
  const labels = {
    en: {
      title: 'Curriculum Vitae',
      personalInfo: 'Personal Information',
      contact: 'Contact',
      dob: 'Date of Birth',
      nationality: 'Nationality',
      gender: 'Gender',
      address: 'Address',
      languages: 'Languages',
      speaking: 'Speaking',
      reading: 'Reading',
      writing: 'Writing',
      listening: 'Listening',
      workExperience: 'Work Experience',
      education: 'Education & Qualifications',
      certificates: 'Licences & Certifications',
      aboutMe: 'Profile Summary',
      present: 'Present',
      noWork: 'No work experience added.',
      noEdu: 'No education added.',
      noCert: 'No certificates added.',
      noLang: 'No languages added.',
      download: 'Download as PDF',
      ready: 'Your EU CV is ready for download',
      footer: 'Generated by Polyglots Talent Portal',
      degree: 'Degree/Level',
      institution: 'Institution',
    },
    de: {
      title: 'Curriculum Vitae',
      personalInfo: 'Persönliche Informationen',
      contact: 'Kontakt',
      dob: 'Geburtsdatum',
      nationality: 'Staatsangehörigkeit',
      gender: 'Geschlecht',
      address: 'Adresse',
      languages: 'Sprachkenntnisse',
      speaking: 'Sprechen',
      reading: 'Lesen',
      writing: 'Schreiben',
      listening: 'Hören',
      workExperience: 'Berufserfahrung',
      education: 'Ausbildung & Qualifikationen',
      certificates: 'Lizenzen & Zertifikate',
      aboutMe: 'Profilzusammenfassung',
      present: 'Heute',
      noWork: 'Keine Berufserfahrung eingetragen.',
      noEdu: 'Keine Ausbildung eingetragen.',
      noCert: 'Keine Zertifikate eingetragen.',
      noLang: 'Keine Sprachen eingetragen.',
      download: 'Als PDF herunterladen',
      ready: 'Ihr EU-Lebenslauf ist bereit zum Herunterladen',
      footer: 'Erstellt von Polyglots Talent Portal',
      degree: 'Abschluss/Stufe',
      institution: 'Institution',
    }
  };
  const L = labels[lang] || labels.en;
  const locale = lang === 'de' ? 'de-DE' : 'en-GB';

  // Format date helper
  const formatDate = (date) => {
    if (!date) return '';
    try {
      return new Date(date).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch {
      return date;
    }
  };

  // Get data arrays
  const employment = dashboardData?.employment_history || user?.employment_history || [];
  const education = dashboardData?.education_history || user?.education_history || [];
  const languages = dashboardData?.language_certificates || user?.language_certificates || [];
  const certificates = dashboardData?.certificates || user?.certificates || [];

  // Helper to extract address
  const getAddress = () => {
    const parts = [];
    if (user?.street_address) parts.push(user.street_address);
    if (user?.city) parts.push(user.city);
    if (user?.state) parts.push(user.state);
    if (user?.postal_code) parts.push(user.postal_code);
    if (user?.country) parts.push(user.country);
    return parts.join(', ');
  };
  const fullAddress = getAddress();

  // Build employment HTML (right panel)
  const employmentHTML = employment.length > 0 ? employment.map(job => `
    <div class="timeline-item">
      <div class="timeline-dot"></div>
      <div class="timeline-content">
        <div class="timeline-date">${formatDate(job.start_date)} – ${job.no_end_date || job.is_current ? L.present : formatDate(job.end_date)}</div>
        <div class="timeline-title">${job.title || ''}</div>
        <div class="timeline-sub">${job.company_name || job.company || ''}</div>
        ${job.description ? `<div class="timeline-desc">${job.description}</div>` : ''}
      </div>
    </div>
  `).join('') : `<p class="empty-text">${L.noWork}</p>`;

  // Build education HTML (right panel)
  const educationHTML = education.length > 0 ? education.map(edu => `
    <div class="timeline-item">
      <div class="timeline-dot"></div>
      <div class="timeline-content">
        <div class="timeline-date">${formatDate(edu.start_date)} – ${formatDate(edu.end_date || edu.year_of_passout)}</div>
        <div class="timeline-title">${edu.course_level || edu.degree_type || ''}</div>
        <div class="timeline-sub">${edu.course_title || edu.qualification || ''} · ${edu.institution_name || edu.university || ''}</div>
        ${edu.grade ? `<div class="timeline-desc">${lang === 'de' ? 'Note' : 'Grade'}: ${edu.grade}</div>` : ''}
      </div>
    </div>
  `).join('') : `<p class="empty-text">${L.noEdu}</p>`;

  // Build languages table (sidebar)
  const languagesSidebarHTML = languages.length > 0 ? languages.map(langItem => `
    <div class="lang-sidebar-item">
      <div class="lang-sidebar-name">${langItem.language || ''}</div>
      <div class="lang-sidebar-levels">
        <span class="lang-level-chip">${L.speaking}: ${langItem.speaking || langItem.speaking_level || langItem.level || '–'}</span>
        <span class="lang-level-chip">${L.reading}: ${langItem.reading || langItem.reading_level || langItem.level || '–'}</span>
        <span class="lang-level-chip">${L.writing}: ${langItem.writing || langItem.writing_level || langItem.level || '–'}</span>
        <span class="lang-level-chip">${L.listening}: ${langItem.listening || langItem.listening_level || langItem.level || '–'}</span>
      </div>
    </div>
  `).join('') : `<p class="sidebar-empty">${L.noLang}</p>`;

  // Build certificates HTML (right panel)
  const certificatesHTML = certificates.length > 0 ? certificates.map(cert => `
    <div class="cert-item">
      <div class="cert-title">${cert.license_title || cert.title || cert.name || ''}</div>
      <div class="cert-meta">
        ${cert.issued_by || cert.issuing_organization ? `<span class="cert-issuer">${cert.issued_by || cert.issuing_organization}</span>` : ''}
        ${cert.issuance_date || cert.issue_date ? `<span class="cert-date">${formatDate(cert.issuance_date || cert.issue_date)}</span>` : ''}
      </div>
    </div>
  `).join('') : `<p class="empty-text">${L.noCert}</p>`;

  // Display name
  const displayName = isMasked ? (user?.username || 'Candidate') : (user?.name || 'Name');
  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  // Build the complete Europass-style HTML document
  const htmlContent = `
    <!DOCTYPE html>
    <html lang="${lang}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${displayName} – ${L.title}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }

        body {
          font-family: Arial, Helvetica, sans-serif;
          background: #f0f2f5;
          color: #1a2332;
          font-size: 13px;
          line-height: 1.55;
        }

        /* ---- Download Bar ---- */
        .download-bar {
          position: fixed; top: 0; left: 0; right: 0;
          background: linear-gradient(135deg, #164194, #0055cc);
          padding: 12px 40px;
          display: flex; justify-content: space-between; align-items: center;
          z-index: 200;
          box-shadow: 0 2px 12px rgba(0,0,0,0.25);
        }
        .download-bar-text { color: white; font-weight: 600; font-size: 14px; }
        .download-btn {
          background: white; color: #164194;
          border: none; padding: 9px 22px; border-radius: 6px;
          font-weight: 700; cursor: pointer; font-size: 13px;
          transition: transform 0.15s;
        }
        .download-btn:hover { transform: translateY(-1px); box-shadow: 0 3px 10px rgba(0,0,0,0.15); }
        .spacer { height: 55px; }

        /* ---- Page Wrapper ---- */
        .cv-page {
          max-width: 880px;
          margin: 20px auto;
          display: flex;
          min-height: 1100px;
          box-shadow: 0 4px 24px rgba(0,51,153,0.12);
          border-radius: 6px;
          overflow: hidden;
        }

        /* ---- Left Sidebar ---- */
        .cv-sidebar {
          width: 33%;
          min-width: 270px;
          flex-shrink: 0;
          background: #164194;
          color: #e8eeff;
          padding: 36px 22px 36px 22px;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .sidebar-photo {
          width: 110px; height: 110px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid rgba(255,255,255,0.35);
          display: block;
          margin: 0 0 16px 0;
        }
        .sidebar-initials {
          width: 110px; height: 110px;
          border-radius: 50%;
          background: rgba(255,255,255,0.18);
          color: white;
          font-size: 38px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          margin: 0 0 16px 0;
          border: 3px solid rgba(255,255,255,0.35);
        }

        .cv-name-sidebar {
          font-size: 20px;
          font-weight: 700;
          color: white;
          text-align: left;
          margin-bottom: 8px;
          line-height: 1.3;
          border-bottom: 2px solid rgba(255,255,255,0.2);
          padding-bottom: 8px;
        }

        .section-divider {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.18);
          margin: 18px 0;
        }

        .sidebar-section-title {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: rgba(255,255,255,0.55);
          margin-bottom: 10px;
        }

        .sidebar-info-row {
          display: flex;
          flex-direction: column;
          margin-bottom: 9px;
        }
        .sidebar-info-label {
          font-size: 10px;
          color: rgba(255,255,255,0.5);
          text-transform: uppercase;
          letter-spacing: 0.8px;
          font-weight: 600;
        }
        .sidebar-info-value {
          color: rgba(255,255,255,0.92);
          font-size: 13px;
          word-break: break-word;
        }

        .lang-sidebar-item {
          margin-bottom: 12px;
        }
        .lang-sidebar-name {
          color: white;
          font-weight: 700;
          font-size: 13px;
          margin-bottom: 4px;
        }
        .lang-sidebar-levels {
          display: flex;
          flex-wrap: wrap;
          gap: 3px;
        }
        .lang-level-chip {
          background: rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.85);
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 3px;
          white-space: nowrap;
        }
        .sidebar-empty {
          color: rgba(255,255,255,0.45);
          font-size: 12px;
          font-style: italic;
        }

        /* ---- Right Main Panel ---- */
        .cv-main {
          flex: 1;
          background: white;
          padding: 36px 36px 36px 32px;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .cv-title-bar {
          margin-bottom: 28px;
          padding-bottom: 14px;
          border-bottom: 3px solid #164194;
        }
        .cv-full-name {
          font-size: 28px;
          font-weight: 700;
          color: #164194;
          letter-spacing: -0.5px;
          line-height: 1.2;
        }
        .cv-subtitle {
          font-size: 12px;
          color: #6b7a99;
          margin-top: 4px;
          font-weight: 400;
        }

        .section {
          margin-bottom: 26px;
        }
        .section-title {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: #164194;
          padding-bottom: 6px;
          border-bottom: 1.5px solid #e1e7f5;
          margin-bottom: 14px;
        }
        .about-text {
          color: #3b4a62;
          font-size: 13px;
          line-height: 1.6;
        }

        /* Timeline items */
        .timeline-item {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }
        .timeline-dot {
          flex-shrink: 0;
          width: 10px; height: 10px;
          background: #164194;
          border-radius: 50%;
          margin-top: 5px;
        }
        .timeline-content { flex: 1; }
        .timeline-date {
          font-size: 11px;
          color: #164194;
          font-weight: 600;
          margin-bottom: 2px;
        }
        .timeline-title {
          font-size: 14px;
          font-weight: 700;
          color: #1a2332;
        }
        .timeline-sub {
          font-size: 12px;
          color: #6b7a99;
          margin-bottom: 3px;
        }
        .timeline-desc {
          font-size: 12px;
          color: #4a5670;
          margin-top: 4px;
          line-height: 1.5;
        }

        /* Certificates */
        .cert-item {
          padding: 8px 10px;
          background: #f5f7ff;
          border-left: 3px solid #164194;
          margin-bottom: 8px;
          border-radius: 0 4px 4px 0;
        }
        .cert-title {
          font-weight: 600;
          color: #1a2332;
          font-size: 13px;
        }
        .cert-meta {
          display: flex;
          gap: 12px;
          margin-top: 2px;
        }
        .cert-issuer { color: #6b7a99; font-size: 11px; }
        .cert-date { color: #164194; font-size: 11px; font-weight: 500; }

        .empty-text {
          color: #94a3b8;
          font-style: italic;
          font-size: 12px;
        }

        /* Footer */
        .cv-footer {
          margin-top: 28px;
          padding-top: 12px;
          border-top: 1px solid #e1e7f5;
          text-align: center;
          color: #a0aec0;
          font-size: 11px;
        }
        .cv-footer-brand { color: #164194; font-weight: 600; }

        /* Europass flag strip */
        .eu-flag-strip {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 20px;
        }
        .eu-flag {
          font-size: 20px;
        }
        .eu-label {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: rgba(255,255,255,0.6);
        }

        /* Print */
        @media print {
          @page { margin: 0; size: A4; }
          body { background: white; }
          .download-bar, .spacer { display: none !important; }
          .cv-page {
            margin: 0;
            border-radius: 0;
            box-shadow: none;
            max-width: 100%;
            min-height: 297mm;
          }
        }
      </style>
    </head>
    <body>
      <div class="download-bar no-print">
        <span class="download-bar-text">${L.ready}</span>
        <button class="download-btn" onclick="window.print()">📥 ${L.download}</button>
      </div>
      <div class="spacer no-print"></div>

      <div class="cv-page">

        <!-- LEFT SIDEBAR -->
        <div class="cv-sidebar">
          <div class="eu-flag-strip">
            <span class="eu-flag">🇪🇺</span>
            <span class="eu-label">${L.title}</span>
          </div>

          ${photoUrl
      ? `<img src="${photoUrl}" alt="Photo" class="sidebar-photo" crossorigin="anonymous" />`
      : `<div class="sidebar-initials">${initials}</div>`
    }
          <div class="cv-name-sidebar">${displayName}</div>

          <hr class="section-divider">

          <!-- Personal Info -->
          <div class="sidebar-section-title">${L.personalInfo}</div>

          ${user?.date_of_birth ? `
          <div class="sidebar-info-row">
            <span class="sidebar-info-label">${L.dob}</span>
            <span class="sidebar-info-value">${formatDate(user.date_of_birth)}</span>
          </div>` : ''}

          ${user?.nationality ? `
          <div class="sidebar-info-row">
            <span class="sidebar-info-label">${L.nationality}</span>
            <span class="sidebar-info-value">${user.nationality}</span>
          </div>` : ''}

          ${user?.gender ? `
          <div class="sidebar-info-row">
            <span class="sidebar-info-label">${L.gender}</span>
            <span class="sidebar-info-value">${user.gender}</span>
          </div>` : ''}

          ${!isMasked && fullAddress ? `
          <div class="sidebar-info-row">
            <span class="sidebar-info-label">${L.address}</span>
            <span class="sidebar-info-value">${fullAddress}</span>
          </div>` : ''}

          ${!isMasked ? `
          <hr class="section-divider">
          <div class="sidebar-section-title">${L.contact}</div>
          ${user?.email ? `<div class="sidebar-info-row">
            <span class="sidebar-info-label">Email</span>
            <span class="sidebar-info-value">${user.email}</span>
          </div>` : ''}
          ${user?.phone ? `<div class="sidebar-info-row">
            <span class="sidebar-info-label">Tel</span>
            <span class="sidebar-info-value">${user.phone}</span>
          </div>` : ''}` : ''}

          <hr class="section-divider">

          <!-- Languages in sidebar -->
          <div class="sidebar-section-title">${L.languages}</div>
          ${languagesSidebarHTML}
        </div>

        <!-- RIGHT MAIN PANEL -->
        <div class="cv-main">
          <div class="cv-title-bar">
            <div class="cv-full-name">${displayName}</div>
            <div class="cv-subtitle">${L.title}</div>
          </div>

          ${(user?.about_me || user?.bio) ? `
          <div class="section">
            <div class="section-title">${L.aboutMe}</div>
            <p class="about-text">${user.about_me || user.bio}</p>
          </div>` : ''}

          <!-- Work Experience -->
          <div class="section">
            <div class="section-title">${L.workExperience}</div>
            ${employmentHTML}
          </div>

          <!-- Education -->
          <div class="section">
            <div class="section-title">${L.education}</div>
            ${educationHTML}
          </div>

          <!-- Certificates -->
          <div class="section">
            <div class="section-title">${L.certificates}</div>
            ${certificatesHTML}
          </div>

          <div class="cv-footer">
            <span>${L.footer} · polyglots.de</span>
          </div>
        </div>

      </div>
    </body>
    </html>
  `;

  // Write to the new window and trigger print
  printWindow.document.write(htmlContent);
  printWindow.document.close();
};

export default generateProfilePDF;
